import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosConfig';
import {
  FiAlertOctagon, FiRefreshCw, FiCalendar, FiUser,
  FiMail, FiMapPin, FiGlobe, FiMessageSquare, FiDownload
} from 'react-icons/fi';

const fmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit'
});
const fmtDate = (iso) => {
  try { return iso ? fmt.format(new Date(iso)) : '—'; } catch { return iso || '—'; }
};

const CARDS = [
  { bg: '#fff7ed', border: '#fb923c', icon: '📅', label: 'Today' },
  { bg: '#fefce8', border: '#facc15', icon: '🕐', label: 'Yesterday' },
  { bg: '#eff6ff', border: '#60a5fa', icon: '📆', label: 'This Month' },
  { bg: '#f0fdf4', border: '#4ade80', icon: '📊', label: 'Total (All Time)' },
];

const PAGE_SIZE = 25;

export default function AdminDisqualifiedLeads() {
  const [campaigns, setCampaigns]   = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [leads, setLeads]           = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);

  /* ── campaigns ── */
  useEffect(() => {
    axios.get('/api/admin/campaigns').then(r => {
      const list = r.data?.campaigns || [];
      setCampaigns(list);
      const stored = localStorage.getItem('selectedCampaign');
      const def = list.find(c => c.campaignId === stored) || list[0];
      if (def) setCampaignId(def.campaignId);
    }).catch(console.error);
  }, []);

  /* ── fetch leads ── */
  const fetchLeads = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ campaignId, limit: 2000 });
      if (startDate) params.append('startDate', startDate);
      if (endDate)   params.append('endDate', endDate);
      const r = await axios.get(`/api/admin/disqualified-leads?${params}`);
      if (r.data.success) {
        setLeads(r.data.leads || []);
        setSummary(r.data.summary || null);
        setPage(1);
      } else { setError(r.data.error || 'Unknown error'); }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [campaignId, startDate, endDate]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  /* ── search filter ── */
  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ['firstName','lastName','email','ipAddress','message','state']
      .some(f => (l[f] || '').toLowerCase().includes(q));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── CSV export ── */
  const exportCSV = () => {
    const cols = ['createdAt','firstName','lastName','email','dob','ssn','mbi','ipAddress','state','zipcode','gender','message'];
    const rows = [cols.join(','), ...filtered.map(l =>
      cols.map(c => {
        // ssn: new records use 'ssn', old records use 'ssnLast4'
        const val = c === 'ssn' ? (l.ssn || l.ssnLast4 || '') : (l[c] || '');
        return `"${String(val).replace(/"/g,'""')}"`;
      }).join(',')
    )];
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' })),
      download: `disqualified-leads-${campaignId}-${new Date().toISOString().slice(0,10)}.csv`
    });
    a.click();
  };

  const summaryVals = summary
    ? [summary.today, summary.yesterday, summary.thisMonth, summary.total]
    : [0, 0, 0, 0];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FiAlertOctagon size={28} color="#ef4444" />
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#1e293b' }}>Disqualified Leads</h1>
        </div>
        <select value={campaignId} onChange={e => { setCampaignId(e.target.value); setPage(1); }}
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', fontWeight: 600, background: '#fff', cursor: 'pointer', color: '#1e293b', minWidth: '160px' }}>
          {campaigns.map(c => <option key={c.campaignId} value={c.campaignId}>{(c.name||c.campaignId).toUpperCase()}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(175px,1fr))', gap: '16px', marginBottom: '28px' }}>
        {CARDS.map((card, i) => (
          <div key={card.label} style={{ background: card.bg, border: `2px solid ${card.border}`, borderRadius: '12px', padding: '20px 16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>{card.icon}</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{loading ? '…' : (summaryVals[i]??0).toLocaleString()}</div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <input type="text" placeholder="Search name, email, IP, state, message…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: '1 1 240px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', outline: 'none' }} />
        <FiCalendar size={16} color="#64748b" />
        <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.88rem' }} />
        <span style={{ color: '#94a3b8' }}>to</span>
        <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.88rem' }} />
        {(startDate || endDate) && (
          <button onClick={() => { setStartDate(''); setEndDate(''); setPage(1); }}
            style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.82rem', color: '#64748b' }}>
            Clear dates
          </button>
        )}
        <button onClick={fetchLeads} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.88rem', color: '#475569' }}>
          <FiRefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
        </button>
        <button onClick={exportCSV} disabled={filtered.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #86efac', background: '#f0fdf4', cursor: filtered.length === 0 ? 'not-allowed' : 'pointer', fontSize: '0.88rem', color: '#15803d', fontWeight: 600 }}>
          <FiDownload size={14} /> Export CSV
        </button>
      </div>

      <div style={{ marginBottom: '10px', color: '#64748b', fontSize: '0.88rem' }}>
        Showing <strong>{paginated.length}</strong> of <strong>{filtered.length}</strong> records
        {search && ` matching "${search}"`}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', marginBottom: '16px' }}>⚠️ {error}</div>
      )}


      {/* Table */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
            <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '12px' }}>Loading disqualified leads…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
            <FiAlertOctagon size={40} style={{ opacity: 0.3 }} />
            <p style={{ marginTop: '12px', fontSize: '1rem' }}>No disqualified leads found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {['#','Name','Email','DOB','SSN','MBI','IP Address','State','Zip','Message','Date / Time'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((lead, idx) => (
                  <tr key={lead.disqualifiedId}
                    style={{ borderBottom: '1px solid #f1f5f9', background: idx%2===0?'#fff':'#fafafa' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?'#fff':'#fafafa'}>
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontWeight: 500 }}>{(page-1)*PAGE_SIZE+idx+1}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <FiUser size={13} color="#94a3b8" />{lead.firstName||'—'} {lead.lastName||''}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <FiMail size={13} color="#94a3b8" />{lead.email||<span style={{color:'#cbd5e1'}}>—</span>}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{lead.dob||<span style={{color:'#cbd5e1'}}>—</span>}</td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {lead.ssn || lead.ssnLast4 || <span style={{color:'#cbd5e1'}}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {lead.mbi || <span style={{color:'#cbd5e1'}}>—</span>}
                    </td>

                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <FiGlobe size={13} color="#94a3b8" />{lead.ipAddress||<span style={{color:'#cbd5e1'}}>—</span>}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <FiMapPin size={13} color="#94a3b8" />{lead.state||<span style={{color:'#cbd5e1'}}>—</span>}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {lead.zipcode || <span style={{color:'#cbd5e1'}}>—</span>}
                    </td>

                    <td style={{ padding: '10px 14px', color: '#64748b', minWidth: '200px' }}>
                      <span style={{ display:'flex', alignItems:'flex-start', gap:'6px' }}>
                        <FiMessageSquare size={13} color="#94a3b8" style={{ marginTop:'2px', flexShrink:0 }} />
                        <span style={{ wordBreak:'break-word', fontSize:'0.83rem', whiteSpace:'pre-wrap' }}>
                          {lead.message||<span style={{color:'#cbd5e1'}}>—</span>}
                        </span>
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtDate(lead.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'8px', marginTop:'20px', flexWrap:'wrap' }}>
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
            style={{ padding:'7px 14px', borderRadius:'8px', border:'1px solid #d1d5db', background:page===1?'#f8fafc':'#fff', cursor:page===1?'not-allowed':'pointer', color:'#475569', fontSize:'0.88rem' }}>
            ← Prev
          </button>
          {Array.from({length:totalPages},(_,i)=>i+1)
            .filter(p=>p===1||p===totalPages||Math.abs(p-page)<=2)
            .reduce((acc,p,i,arr)=>{ if(i>0&&p-arr[i-1]>1) acc.push('…'); acc.push(p); return acc; },[])
            .map((p,i)=> p==='…'
              ? <span key={`e${i}`} style={{color:'#94a3b8',padding:'0 4px'}}>…</span>
              : <button key={p} onClick={()=>setPage(p)}
                  style={{ padding:'7px 12px', borderRadius:'8px', border:`1px solid ${p===page?'#3b82f6':'#d1d5db'}`, background:p===page?'#3b82f6':'#fff', color:p===page?'#fff':'#475569', cursor:'pointer', fontSize:'0.88rem', fontWeight:p===page?700:400 }}>
                  {p}
                </button>
            )}
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
            style={{ padding:'7px 14px', borderRadius:'8px', border:'1px solid #d1d5db', background:page===totalPages?'#f8fafc':'#fff', cursor:page===totalPages?'not-allowed':'pointer', color:'#475569', fontSize:'0.88rem' }}>
            Next →
          </button>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

